import type { ReactNode } from 'react';
import { Bot, Lightbulb, Plug, Settings, PanelLeftClose, LogOut, Plus, Moon, Sun, Monitor, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/utils/cn';
import { SessionHistoryList } from '@/components/sessions/SessionHistoryList';
import { getStoredTheme, setTheme, type ThemeMode } from '@/services/theme';
import { useState, useEffect } from 'react';
import HAIAssistantsLogo from '@/components/ui/hai-assistants-logo';
import { getAppConfig } from '@/config/app-config';

interface AppSidebarProps {
  open: boolean;
  currentView: 'agent-selection' | 'agent-editor' | 'chat' | 'skills' | 'settings' | 'connectors';
  currentSessionId?: string | null;
  workspaceId: string | null;
  user?: { name: string; email: string; avatar?: string } | null;
  onClose: () => void;
  onNavigate: (view: 'agent-selection' | 'skills' | 'connectors' | 'settings') => void;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onSignOut: () => void;
}

export function AppSidebar({
  open,
  currentView,
  currentSessionId,
  workspaceId,
  user,
  onClose,
  onNavigate,
  onSessionSelect,
  onNewChat,
  onSignOut,
}: AppSidebarProps) {
  const { enableSettingsPage, haiUiBaseUrl } = getAppConfig();

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 w-72 border-r border-border bg-background/95 backdrop-blur flex flex-col transform transition-transform duration-200 ease-out shadow-lg',
        open ? 'translate-x-0' : '-translate-x-full'
      )}
      aria-hidden={!open}
    >
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <HAIAssistantsLogo variant="with-presidio" height={24} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="size-7"
          title="Hide sidebar"
        >
          <PanelLeftClose className="size-4" strokeWidth={2.2} />
        </Button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {/* Hidden for now — re-enable when ready
        <div className="p-2 space-y-1">
          <SidebarNavButton
            active={currentView === 'agent-selection'}
            icon={<Bot className="size-3.5" />}
            label="Agents"
            onClick={() => onNavigate('agent-selection')}
          />
          <SidebarNavButton
            active={currentView === 'skills'}
            icon={<Lightbulb className="size-3.5" />}
            label="Skills"
            onClick={() => onNavigate('skills')}
          />
          <SidebarNavButton
            active={currentView === 'connectors'}
            icon={<Plug className="size-3.5" />}
            label="Connectors"
            onClick={() => onNavigate('connectors')}
          />
          {enableSettingsPage && (
            <SidebarNavButton
              active={currentView === 'settings'}
              icon={<Settings className="size-3.5" />}
              label="Settings"
              onClick={() => onNavigate('settings')}
            />
          )}
        </div>
        */}

        {workspaceId && (
          <a
            href={`${haiUiBaseUrl}/workspace/${workspaceId}/assistants`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between gap-2 px-3 py-2 mx-2 mt-2 rounded-md text-xs font-medium bg-primary/5 text-primary hover:bg-primary/10 border border-primary/10 transition-colors"
          >
            <span className="flex items-center gap-2">
              <ExternalLink className="size-3.5" />
              Open in AI Studio
            </span>
            <ArrowRight className="size-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </a>
        )}

        <div className="px-3 pt-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          Chats
        </div>
        <div className="p-2">
          <Button
            onClick={onNewChat}
            className="w-full justify-start gap-2 text-xs h-8"
            size="sm"
          >
            <Plus className="size-4" />
            New Chat
          </Button>
        </div>

        <SessionHistoryList
          className="flex-1"
          workspaceId={workspaceId}
          currentSessionId={currentSessionId ?? undefined}
          visible={open}
          onSessionSelect={onSessionSelect}
        />
      </div>

      <div className="p-3 border-t border-border space-y-2">
        {user && (
          <div className="flex items-center gap-2 rounded-md border border-border/60 bg-card/80 px-2.5 py-2">
            <Avatar className="size-7 ring-2 ring-primary/10">
              {user.avatar && <AvatarImage src={user.avatar} />}
              <AvatarFallback className="text-[10px]">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">{user.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        )}
        <ThemeToggle />
        {/* Hidden for now — re-enable when ready
        <Button
          variant="outline"
          className="w-full justify-start gap-2 text-xs"
          onClick={onSignOut}
        >
          <LogOut className="size-3.5" />
          Sign out
        </Button>
        */}
      </div>
    </aside>
  );
}

function SidebarNavButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
    className={cn(
      'w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs transition-colors',
      active
        ? 'bg-primary/10 text-primary font-medium'
        : 'hover:bg-accent hover:text-foreground text-foreground hover:ring-1 hover:ring-border'
    )}
    >
      {icon}
      <span className="text-left">{label}</span>
    </button>
  );
}

function ThemeToggle() {
  const [theme, setThemeState] = useState<ThemeMode>('system');

  useEffect(() => {
    setThemeState(getStoredTheme());
  }, []);

  const handleThemeChange = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    setTheme(newTheme);
  };

  const themeConfig = {
    light: { icon: Sun, label: 'Light mode' },
    dark: { icon: Moon, label: 'Dark mode' },
    system: { icon: Monitor, label: 'System theme' },
  };

  return (
    <div className="flex items-center justify-center gap-1 p-1 rounded-md bg-muted/40">
      {(Object.keys(themeConfig) as ThemeMode[]).map((mode) => {
        const { icon: Icon, label } = themeConfig[mode];
        return (
          <button
            key={mode}
            type="button"
            onClick={() => handleThemeChange(mode)}
            title={label}
            className={cn(
              'flex-1 p-1.5 rounded transition-all',
              theme === mode
                ? 'bg-background shadow-sm text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
          >
            <Icon className="size-3.5 mx-auto" strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );
}
